import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { GET, PGET, POST } from "helpers/api";
import { formatDateBackend, formatDateWithTime, formatMoney } from "helpers/helpers";
import { Dropdown, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import CreateUpdateModal from "../documentsIn/CreateUpdateModal";
import { toast } from "react-toastify";

const Chip = ({ children, color = "#6c757d", size = "md" }) => (
	<span
		className={`badge-chip ${size === "xs" ? "badge-xs" : ""}`}
		style={{ backgroundColor: color, color: "#fff" }}
	>
		{children}
	</span>
);

function Index() {
	const { t } = useTranslation();
	const today = new Date();

	const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
	const [data, setData] = useState([]);

	// Reception modal
	const [show, setShow] = useState(false);
	const [modalData, setModalData] = useState(null);

	// Approve/Reject modals
	const [showApproveModal, setShowApproveModal] = useState(false);
	const [showRejectModal, setShowRejectModal] = useState(false);
	const [modalRow, setModalRow] = useState(null);
	const [rejectComment, setRejectComment] = useState("");

	const [filterData, setFilterData] = useState({
		startDate: formatDateBackend(new Date(today.getFullYear(), today.getMonth() - 1, today.getDate())),
		endDate: formatDateBackend(new Date()),
	});

	// Multicard visibility: only for hp / alisher0404
	const authUserRaw = localStorage.getItem('authUser');
	let enableSdOrder = false;
	try {
		const u = JSON.parse(authUserRaw || '{}');
		const username = (u?.username || '');
		enableSdOrder = ['hp', 'alisher0404', 'alisher202504'].includes(username);
	} catch (_) {
		enableSdOrder = false;
	}

	const statusMap = useMemo(
		() => [
			{ id: 1, name: t("STATUS_NEW"), color: "#34C38F" },
			{ id: 2, name: t("STATUS_LOADED"), color: "#0000FF99" },
			{ id: 3, name: t("STATUS_DELIVERED"), color: "#F1B44C" },
			{ id: 4, name: t("STATUS_RETURNED"), color: "gray" },
			{ id: 5, name: t("STATUS_CANCEL"), color: "#FF000099" },
		],
		[t]
	);
	const findStatus = (id) => statusMap.find(s => String(s.id) === String(id)) || { name: "-", color: "#6c757d" };

	async function getData() {
		const res = await GET("services/web/api/sd/getSdOrders", filterData, { loader: true });
		if (!res) { setData([]); return; }

		const [dealers, poses] = await Promise.all([
			GET("/services/web/api/dealer-helper", {}, { loader: false }),
			GET("/services/web/api/pos-helper", {}, { loader: false }),
		]);

		const out = (res.result || []).map(x => {
			const d = dealers?.find?.(v => String(v.id) === String(x.dealerId));
			const p = poses?.find?.(v => String(v.id) === String(x.posId));
			return { ...x, dealerName: d?.name, posName: p?.name };
		}).reverse();

		setData(out);
	}
	useEffect(() => { getData(); }, []); // on mount

	const openReceptionModal = async (row) => {
		setModalData(row);
		setShow(true);
	};

	// ---- Approve / Reject handlers ----
	const openApproveModal = (row) => { setModalRow(row); setShowApproveModal(true); };
	const openRejectModal = (row) => { setModalRow(row); setRejectComment(""); setShowRejectModal(true); };

	const approveOrder = async () => {
		try {
			await POST("/services/web/api/sd-update-order-status", {
				order_id: modalRow?.order_id,
				pos_id: modalRow?.posId,
				dealer_id: modalRow.dealerId,
				status: "approved",
			}, { loader: true });
			setShowApproveModal(false);
			setModalRow(null);
			await getData();
			toast.success(t("SUCCESS"));
		} catch (e) {
			console.error(e);
			toast.error(t("ERROR"));
		}
	};

	const rejectOrder = async () => {
		if (!rejectComment?.trim()) {
			toast.warning(t("COMMENT_REQUIRED"));
			return;
		}
		try {
			await POST("/services/web/api/sd-update-order-status", {
				order_id: modalRow?.order_id,
				pos_id: modalRow?.posId,
				dealer_id: modalRow.dealerId,
				status: "rejected",
				comment: rejectComment.trim(),
			}, { loader: true });
			setShowRejectModal(false);
			setModalRow(null);
			setRejectComment("");
			await getData();
			toast.success(t("SUCCESS"));
		} catch (e) {
			console.error(e);
			toast.error(t("ERROR"));
		}
	};

	const renderStatusCell = (row) => {
		const s = findStatus(row.status);
		const acc = String(row?.acceptStatus || "").toLowerCase();

		return (
			<div className="status-stack">
				<Chip color={s.color}>{s.name}</Chip>
				{/* Optional second chip to show acceptStatus itself */}
				{acc && acc !== "pending" && (
					<Chip size="xs" color={acc === "approved" ? "#28a745" : acc === "rejected" ? "#dc3545" : "#6c757d"}>
						{t(acc.toUpperCase())}
					</Chip>
				)}
			</div>
		);
	};

	const renderActions = (row) => {
		const details = Array.isArray(row?.details) ? row.details : [];
		const hasNullUom = details.some(d => d?.uom_id == null);

		// Keep your “reception” logic as-is
		const canReceive = Number(row?.status) === 3 && !row?.confirmed && !hasNullUom;

		return (
			<div className="d-inline-flex align-items-center gap-1">
				{/* Qabul qilish */}
				{canReceive && (
					<button
						className="btn btn-success btn-icon"
						title={t("GOOD_RECEPTION")}
						onClick={() => openReceptionModal(row)}
					>
						<i className="uil uil-box" />
					</button>
				)}

				{/* UOM yo‘q indikator */}
				{Number(row?.status) === 3 && hasNullUom && (
					<span className="dot dot-danger" title={t("EXTERNAL_ORDER_NOTIFICATION")} />
				)}

				{/* Ko‘rish */}
				<Link
					to={`/sd-purchase/view/${row.order_id}`}
					state={{ details: row }}
					className="btn btn-outline-primary btn-icon"
					title={t("VIEW")}
				>
					<i className="uil uil-eye" />
				</Link>
			</div>
		);
	};

	const renderActionsDealer = (row) => {

		// SHOW approve/reject ONLY when acceptStatus === "pending"
		const isAcceptPending = String(row?.accept_status || "").toLowerCase() === "pending" && row?.status === "1" && row?.rejectable;

		return (
			<div className="d-inline-flex align-items-center gap-1">
				{/* Approve / Reject — ONLY if acceptStatus is pending */}
				{!row?.confirmed && isAcceptPending && (
					<button
						className="btn btn-success btn-icon"
						title={t("APPROVE")}
						onClick={() => openApproveModal(row)}
					>
						<i className="uil uil-check" />
					</button>
				)}
				{!row?.confirmed && isAcceptPending && (
					<button
						className="btn btn-outline-danger btn-icon"
						title={t("REJECT")}
						onClick={() => openRejectModal(row)}
					>
						<i className="uil uil-times" />
					</button>
				)}
			</div>
		);
	};

	return (
		<>
			{show && (
				<CreateUpdateModal
					product={modalData}
					callbakc_func={(s) => { setShow(s); getData(); }}
				/>
			)}

			{/* APPROVE MODAL */}
			<Modal show={showApproveModal} onHide={() => setShowApproveModal(false)} centered backdrop="static">
				<Modal.Header closeButton>
					<Modal.Title>{t("CONFIRM_ORDER")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="mb-4">{t("CONFIRM_ORDER_CONFIRM")}</p>
					<div className="d-flex justify-content-end gap-2">
						<button className="btn btn-success" onClick={approveOrder}>
							{t("CONFIRM")}
						</button>
					</div>
				</Modal.Body>
			</Modal>

			{/* REJECT MODAL */}
			<Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered backdrop="static">
				<Modal.Header closeButton>
					<Modal.Title>{t("CANCEL_ORDER")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="mb-3">{t("CANCEL_ORDER_CONFIRM")}</p>
					<div className="form-group">
						<label className="form-label">{t("COMMENT")}</label>
						<textarea
							className="form-control"
							rows={3}
							value={rejectComment}
							onChange={(e) => setRejectComment(e.target.value)}
						/>
					</div>
					<div className="d-flex justify-content-end gap-2 mt-3">
						<button className="btn btn-danger" onClick={rejectOrder}>
							{t("CONFIRM_CANCEL")}
						</button>
					</div>
				</Modal.Body>
			</Modal>

			<div className="page-header d-flex justify-content-between mb-2">
				<h4 className="vertical-center">{t("ORDERS_SD")}</h4>
				{
					enableSdOrder &&
					<div className="d-flex">
						<Link className="btn-rounded btn btn-primary" to="/sd-purchase/create">
							<i className="uil uil-plus me-2"></i>
							{t('CREATE')}
						</Link>
					</div>
				}

			</div>

			<div className="card">
				<div className="card-body">
					{/* Filter */}
					<div className="d-flex mb-3">
						<Dropdown onToggle={() => setDropdownIsOpen(p => !p)} show={dropdownIsOpen}>
							<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100 me-2">
								{t("FILTER")}
							</Dropdown.Toggle>
							<Dropdown.Menu className="dropdown-filter p-3" onClick={(e) => e.stopPropagation()}>
								<div className="row g-3">
									<div className="col-md-6">
										<label>{t("PERIOD")}</label>
										<div className="calendar-input">
											<DatePicker
												className="form-control"
												dateFormat="dd.MM.yyyy"
												selected={filterData.startDate ? new Date(filterData.startDate) : null}
												onChange={(d) => setFilterData(s => ({ ...s, startDate: formatDateBackend(d) }))}
											/>
											<i className="uil uil-calendar-alt"></i>
										</div>
									</div>
									<div className="col-md-6">
										<label className="invisible">.</label>
										<div className="calendar-input">
											<DatePicker
												className="form-control"
												dateFormat="dd.MM.yyyy"
												selected={filterData.endDate ? new Date(filterData.endDate) : null}
												onChange={(d) => setFilterData(s => ({ ...s, endDate: formatDateBackend(d) }))}
											/>
											<i className="uil uil-calendar-alt"></i>
										</div>
									</div>
								</div>
								<div className="d-flex justify-content-end mt-3">
									<button className="btn btn-secondary me-2" onClick={() => setDropdownIsOpen(false)}>{t("CANCEL")}</button>
									<button
										className="btn btn-primary"
										onClick={async () => { await getData(); setDropdownIsOpen(false); }}
									>
										{t("APPLY")}
									</button>
								</div>
							</Dropdown.Menu>
						</Dropdown>
					</div>

					{/* Table */}
					<div className="table-responsive">
						<table className="table table-striped table-hover align-middle mb-0">
							<thead className="table-light">
								<tr>
									<th>{t("ID")}</th>
									<th>{t("POS")}</th>
									<th>{t("DEALER")}</th>
									<th className="text-center">{t("QUANTITY")}</th>
									<th className="text-center">{t("AMOUNT")}</th>
									<th className="text-center">{t("DATE_OF_RECEIPT")}</th>
									<th className="text-center status-col">{t("STATUS")}</th>
									<th className="text-center action-col">{t("ACTION")}</th>
									<th className="text-center action-col">{t("ACTION")}({t("DEALER")})</th>
								</tr>
							</thead>
							<tbody>
								{data?.length ? data.map((row) => (
									<tr key={row.order_id}>
										<td><strong>{row.order_id}</strong></td>
										<td>{row.posName || "-"}</td>
										<td>{row.dealerName || "-"}</td>
										<td className="text-center">{row.count ?? 0}</td>
										<td className="text-center">{formatMoney(row.summa)}</td>
										<td className="text-center">{row.date ? formatDateWithTime(row.date) : "-"}</td>
										<td className="text-center status-col">{renderStatusCell(row)}</td>
										<td className="text-center action-col">{renderActions(row)}</td>
										<td className="text-center action-col">{renderActionsDealer(row)}</td>
									</tr>
								)) : (
									<tr><td colSpan="8" className="text-center">{t("NO_DATA")}</td></tr>
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</>
	);
}

export default Index;
